import { UploadService } from "@/services/APIs";
import { axiosConfig } from "@/services/axiosConfig";
import {
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Button, Upload } from "antd";
import type { UploadProps, UploadFile } from "antd/es/upload";
import { type MouseEvent, useEffect, useRef, useState } from "react";
import { useField } from "@formily/react";

const isUUID = (val: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

/** Backend may return a relative path; build a full URL for axios (never rely on the SPA origin). */
function toAbsoluteFileUrl(pathOrUrl: string): string {
  if (!pathOrUrl) {
    return "";
  }
  const trimmed = pathOrUrl.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    if (trimmed.startsWith("http://djangoproject.universal-software.in")) {
      return trimmed.replace(/^http:\/\//i, "https://");
    }
    return trimmed;
  }
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return API_BASE ? `${API_BASE}${path}` : path;
}

/** Same-origin (or relative) media URLs need Authorization — plain window.open / <a href> will 401. */
function shouldFetchWithAuth(pathOrUrl: string): boolean {
  if (!pathOrUrl || !API_BASE) {
    return false;
  }
  try {
    if (pathOrUrl.startsWith("/")) {
      return true;
    }
    const u = new URL(pathOrUrl);
    const base = new URL(API_BASE);
    return u.origin === base.origin;
  } catch {
    return false;
  }
}

async function fetchFileBlob(pathOrUrl: string): Promise<Blob> {
  const url = toAbsoluteFileUrl(pathOrUrl);
  const response = await axiosConfig.get(url, {
    responseType: "blob",
    notifyMessage: false,
  });
  return response.data as Blob;
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename || "download";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

function getRawPathFromUploadFile(file: UploadFile): string {
  const b = (file as UploadFile & { response?: { backendFile?: any } }).response
    ?.backendFile;
  if (typeof b === "string") {
    return b;
  }
  if (b && typeof b === "object") {
    return b.file_path ?? b.url ?? "";
  }
  return file.url ?? "";
}

function displayNameForBackendFile(file: any, pathOrUrl?: string): string {
  const fromMeta =
    file?.file_original_name ?? file?.file_name ?? file?.original_name;
  if (fromMeta && String(fromMeta).trim()) {
    return String(fromMeta).trim();
  }
  const n = file?.name;
  if (n && String(n).trim() && !isUUID(String(n))) {
    return String(n).trim();
  }
  if (pathOrUrl) {
    try {
      const segment = pathOrUrl.split("/").pop()?.split("?")[0] ?? "";
      if (segment) {
        return decodeURIComponent(segment);
      }
    } catch {
      /* ignore */
    }
  }
  return "file";
}

export const CustomFileUpload = (
  props: UploadProps & {
    action?: string;
    value?: any;
    onChange?: (value: any) => void;
    defaultFileList?: File[];
    multiple?: boolean;
  }
) => {
  const { value, onChange, multiple = false, ...restProps } = props;
  // Get direct access to Formily field to set value reliably
  const field = useField<any>();

  // uploadedFiles is our single source of truth for what to DISPLAY
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  // Track whether user has manually uploaded something in this session
  const userUploadedRef = useRef(false);
  const lastValueRef = useRef<string>("");

  useEffect(() => {
    // If parent/form clears value after save, clear UI immediately too.
    if (!value || (Array.isArray(value) && value.length === 0)) {
      userUploadedRef.current = false;
      lastValueRef.current = "";
      setUploadedFiles(prev => (prev.length > 0 ? [] : prev));
      return;
    }

    // Once user has uploaded a file, don't let the value prop override it
    // (because value prop lags behind until next render cycle)
    if (userUploadedRef.current) {
      return;
    }

    // Normalize the incoming value
    let raw = value;

    // Parse string value
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch {
        // plain string or UUID — keep as-is
      }
    }

    // Normalize to array
    if (!Array.isArray(raw)) {
      raw = [raw];
    }

    const valueStr = JSON.stringify(raw);
    if (valueStr === lastValueRef.current) {
      return;
    } // no change
    lastValueRef.current = valueStr;

    // Resolve UUIDs to file objects
    const resolve = async () => {
      const resolved = await Promise.all(
        raw.map(async (item: any) => {
          if (typeof item === "string" && isUUID(item)) {
            try {
              const fileData = await UploadService.getFileById({ id: item });
              return fileData;
            } catch {
              return null;
            }
          }
          return item;
        })
      );
      const valid = resolved.filter(Boolean);
      if (valid.length > 0) {
        setUploadedFiles(valid);
      }
    };

    resolve();
  }, [value]);

  const handleFileSelect = async (file: File) => {
    try {
      const response = await UploadService.uploadFiles({
        folderName: "uploads",
        files: [file],
      });

      console.log("[CustomFileUpload] upload API response:", response);

      // Normalize response to an array of items (strings or objects)
      let items: any[] = [];
      if (Array.isArray(response)) {
        items = response;
      } else if (response && typeof response === "object") {
        items = [response]; // single object response
      } else if (typeof response === "string") {
        items = [response]; // plain string URL
      }

      if (items.length > 0) {
        // Extract string URLs or keep objects as-is
        const existingUrls = uploadedFiles.map((f: any) =>
          typeof f === "string" ? f : (f?.file_path ?? f?.url ?? f?.id ?? "")
        );

        const uniqueNew = items.filter((item: any) => {
          const key =
            typeof item === "string"
              ? item
              : (item?.file_path ?? item?.url ?? item?.id ?? "");
          return !existingUrls.includes(key);
        });

        const newValueObjects = multiple
          ? [...uploadedFiles, ...uniqueNew]
          : uniqueNew; // single file mode: replace

        // Extract ONLY IDs for the form value
        const newValueIds = newValueObjects.map((f: any) =>
          typeof f === "string" ? f : (f?.id ?? f?.file_path ?? f?.url ?? "")
        );

        userUploadedRef.current = true;
        setUploadedFiles(newValueObjects);

        // Use both to ensure compatibility
        onChange?.(newValueIds);
        if (field) {
          field.setValue(newValueIds);
        }
      }
    } catch (error) {
      console.error("[CustomFileUpload] Upload failed:", error);
    }
    return false;
  };

  const handleRemove = (file: any) => {
    const fileUrl = file.url ?? file.file_path ?? file.name ?? "";

    const filteredFiles = uploadedFiles.filter((f: any) => {
      const fUrl =
        typeof f === "string" ? f : (f?.file_path ?? f?.url ?? f?.name ?? "");
      return fUrl !== fileUrl;
    });

    userUploadedRef.current = filteredFiles.length > 0;
    setUploadedFiles(filteredFiles);

    const filteredIds = filteredFiles.map((f: any) =>
      typeof f === "string" ? f : (f?.id ?? f?.file_path ?? f?.url ?? "")
    );

    onChange?.(filteredIds);
    if (field) {
      field.setValue(filteredIds);
    }
  };

  const handleChange = (info: any) => {
    if (info.file.status === "removed") {
      handleRemove(info.file);
    }
  };

  const handlePreview = async (file: UploadFile, e?: MouseEvent) => {
    e?.preventDefault();
    const rawPath = getRawPathFromUploadFile(file);
    if (!rawPath) {
      return;
    }

    try {
      if (shouldFetchWithAuth(rawPath)) {
        const blob = await fetchFileBlob(rawPath);
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120_000);
      } else {
        window.open(
          toAbsoluteFileUrl(rawPath),
          "_blank",
          "noopener,noreferrer"
        );
      }
    } catch (err) {
      console.error("[CustomFileUpload] Preview failed:", err);
    }
  };

  const handleDownload = async (file: UploadFile) => {
    const rawPath = getRawPathFromUploadFile(file);
    if (!rawPath) {
      return;
    }
    const backend = (file as UploadFile & { response?: { backendFile?: any } })
      .response?.backendFile;
    const meta = typeof backend === "object" && backend !== null ? backend : {};
    const filename = file.name || displayNameForBackendFile(meta, rawPath);

    try {
      if (shouldFetchWithAuth(rawPath)) {
        const blob = await fetchFileBlob(rawPath);
        triggerBlobDownload(blob, filename);
      } else {
        const absolute = toAbsoluteFileUrl(rawPath);
        try {
          const res = await fetch(absolute);
          const blob = await res.blob();
          triggerBlobDownload(blob, filename);
        } catch {
          window.open(absolute, "_blank", "noopener,noreferrer");
        }
      }
    } catch (err) {
      console.error("[CustomFileUpload] Download failed:", err);
    }
  };

  const fileList: UploadFile[] = (uploadedFiles ?? []).map(
    (file: any, index: number) => {
      // Handle plain string URL (returned by upload API)
      if (typeof file === "string") {
        const filename = displayNameForBackendFile({}, file);
        return {
          uid: `rc-upload-${index}-${file}`,
          name: filename,
          status: "done" as const,
          url: file,
          response: { backendFile: file },
        };
      }

      // Handle file object from backend {id, file_path, file_original_name, ...}
      if (typeof file === "object" && file?.id) {
        const rawPath = file?.file_path ?? file?.url ?? "";
        const name = displayNameForBackendFile(file, rawPath);
        return {
          uid: String(file.id ?? `rc-upload-${index}`),
          name,
          status: "done" as const,
          url: rawPath ?? undefined,
          response: { backendFile: file },
        };
      }

      return {
        uid: `rc-upload-${index}`,
        name: "file",
        status: "done" as const,
        url: "",
      };
    }
  );

  const showUploadButton =
    multiple || !uploadedFiles || uploadedFiles.length === 0;

  return (
    <div className="space-y-2">
      <Upload
        {...restProps}
        beforeUpload={handleFileSelect}
        onChange={handleChange}
        onRemove={handleRemove}
        onDownload={handleDownload}
        maxCount={multiple ? undefined : 1}
        multiple={multiple}
        fileList={fileList}
        showUploadList={{
          showRemoveIcon: true,
          showDownloadIcon: true,
          showPreviewIcon: true,
          downloadIcon: <DownloadOutlined />,
          removeIcon: <DeleteOutlined />,
          previewIcon: <EyeOutlined />,
        }}
        onPreview={handlePreview}
      >
        {showUploadButton && (
          <Button icon={<UploadOutlined />}>
            {multiple ? "Upload Files" : "Upload File"}
          </Button>
        )}
      </Upload>
    </div>
  );
};
